import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageSection } from "@/components/AppLayout.jsx";
import { GsdReadableBlock } from "@/coquille-bridge";
import { AdminGuard } from "@/components/admin/AdminGuard.jsx";
import { DataTable } from "@/components/DataTable.jsx";
import { useKarateUi } from "@/hooks/useKarateUi.js";
import { getDataStore } from "@/data/dataStore.js";
import { useMembershipCopy, useMembershipStore } from "@/membership/useMembership.js";
import { applyCkaOfficialImport, dryRunCkaOfficialImport, getLastCkaImportPlan } from "@/membership/ckaImport/applyCkaOfficialImport.js";
import { CKA_IMPORT_SOURCE, INSTRUCTOR_CATALOG, RICHARD_PIK_ID, TO_COMPLETE } from "@/membership/ckaImport/ckaImportConstants.js";
import { canViewHealthRecord } from "@/membership/healthPermissions.js";
import { useAuth } from "@/context/AuthContext.jsx";

function displayValue(value, fallback) {
  const text = String(value ?? "").trim();
  if (!text || text === TO_COMPLETE) return fallback;
  return text;
}

export function OfficialMembersRoster({ autoApply = false }) {
  const { localizeDemo } = useKarateUi();
  const { copy } = useMembershipCopy();
  const oi = copy.officialImport;
  const store = React.useSyncExternalStore(
    (cb) => {
      window.addEventListener("karate-data-updated", cb);
      return () => window.removeEventListener("karate-data-updated", cb);
    },
    () => getDataStore(),
    () => getDataStore(),
  );
  const membershipStore = useMembershipStore();
  const [plan, setPlan] = useState(() => getLastCkaImportPlan());
  const [message, setMessage] = useState("");
  const alreadyImported = (store.etudiants || []).some((s) => s.source === CKA_IMPORT_SOURCE);

  const runDry = () => {
    dryRunCkaOfficialImport().then(setPlan).catch(() => {});
  };
  const runApply = () => {
    applyCkaOfficialImport()
      .then((result) => {
        setPlan(result.plan);
        setMessage(oi.applied);
      })
      .catch((err) => {
        setMessage(String(err?.message || err));
      });
  };

  React.useEffect(() => {
    if (autoApply && !alreadyImported) {
      runApply();
      return;
    }
    if (alreadyImported && !plan) runDry();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- first mount / after store hydrate
  }, [autoApply, alreadyImported]);

  const instructorName = (id) => store.instructeurs.find((i) => i.id === id)?.nom || id || "—";
  const dojoName = (id) => store.dojos.find((d) => d.id === id)?.nom || id || "—";

  const rows = useMemo(
    () =>
      (store.etudiants || [])
        .filter((s) => s.source === CKA_IMPORT_SOURCE || s.id === RICHARD_PIK_ID)
        .map((s) => ({
          id: s.id,
          name: `${s.prenom} ${s.nom}`.trim(),
          instructor: instructorName(s.instructeur_id || s.linkedInstructorId),
          instructorId: s.instructeur_id || s.linkedInstructorId || "",
          dojo: dojoName(s.dojo_id),
          dojoId: s.dojo_id || "",
          grade: displayValue(s.ceinture, oi.columns.grade === "Grade" ? copy.admin.toComplete : copy.admin.toComplete),
          status: s.statut,
          kind: s.kind === "minor" ? copy.admin.minor : copy.admin.adult,
          membershipId: membershipStore.memberships.find((m) => m.studentId === s.id)?.id,
          flags: (s.reviewFlags || []).join(", "),
          _raw: s,
        })),
    [store.etudiants, store.instructeurs, store.dojos, membershipStore.memberships, copy, oi],
  );

  const instructorFilters = Object.values(INSTRUCTOR_CATALOG).map((i) => ({ value: i.nom, label: i.nom }));
  const dojoFilters = [...new Set(rows.map((r) => r.dojo).filter(Boolean))].map((n) => ({ value: n, label: localizeDemo(n) }));
  const gradeFilters = [...new Set(rows.map((r) => r.grade).filter(Boolean))].map((n) => ({ value: n, label: n }));

  const yannick = plan?.instructorTable?.find((r) => r.key === "yannick-kammer");

  return (
    <div className="membership-page" data-surface="light" data-testid="official-members-roster">
      <p data-testid="official-import-no-login">{oi.noLogin}</p>
      <p data-testid="official-import-health-list-note">{oi.healthHiddenList}</p>
      <div className="btn-row">
        <button type="button" className="btn-secondary" data-testid="cka-import-dry-run" onClick={runDry}>
          {oi.dryRun}
        </button>
        <button type="button" className="btn-primary" data-testid="cka-import-apply" onClick={runApply}>
          {oi.apply}
        </button>
      </div>
      {message || alreadyImported ? (
        <p data-testid="cka-import-applied">{message || oi.applied}</p>
      ) : null}
      {plan ? (
        <div data-testid="cka-import-audit">
          {plan.instructorTable.map((row) => (
            <p key={row.key} data-testid={`cka-audit-${row.key}`}>
              {row.instructor}: {row.sourceRows} / {row.uniqueMembers} / {row.duplicates} / {row.imported}
              {row.zeroFoundNote ? ` — ${oi.yannickZero}` : ""}
            </p>
          ))}
          {yannick && yannick.sourceRows === 0 ? <p data-testid="cka-yannick-zero">{oi.yannickZero}</p> : null}
          <p data-testid="cka-richard-note">{oi.richardAdded}</p>
        </div>
      ) : null}
      {plan?.conflicts?.length ? (
        <div data-testid="cka-import-conflicts">
          <h3>{oi.review}</h3>
          {plan.conflicts.map((c) => (
            <p key={c.sourceRowId}>
              {c.name} — {c.via}
            </p>
          ))}
        </div>
      ) : null}
      <DataTable
        rows={rows}
        columns={[
          {
            key: "name",
            label: oi.columns.name,
            sortable: true,
            render: (r) =>
              r.membershipId ? (
                <Link to={`/admin/adhesions/${r.membershipId}`} data-testid={`official-member-${r.id}`}>
                  {r.name}
                </Link>
              ) : (
                r.name
              ),
          },
          { key: "instructor", label: oi.columns.instructor, sortable: true },
          { key: "dojo", label: oi.columns.dojo, sortable: true },
          { key: "grade", label: oi.columns.grade, sortable: true },
          { key: "status", label: oi.columns.status, sortable: true },
          { key: "kind", label: oi.columns.kind, sortable: true },
        ]}
        searchFields={["name", "instructor", "dojo", "grade", "status", "kind"]}
        filters={[
          { field: "instructor", label: oi.columns.instructor, options: instructorFilters },
          { field: "dojo", label: oi.columns.dojo, options: dojoFilters },
          { field: "grade", label: oi.columns.grade, options: gradeFilters },
          {
            field: "status",
            label: oi.columns.status,
            options: [
              { value: "actif", label: copy.statuses?.active || "actif" },
              { value: "a_confirmer", label: copy.admin.toComplete },
            ],
          },
          {
            field: "kind",
            label: oi.columns.kind,
            options: [
              { value: copy.admin.adult, label: copy.admin.adult },
              { value: copy.admin.minor, label: copy.admin.minor },
            ],
          },
        ]}
        emptyMessage={oi.empty}
        emptyFilteredMessage={oi.emptyFiltered}
        testId="official-members-table"
      />
    </div>
  );
}

export function AdminOfficialMembersPage() {
  const { copy } = useMembershipCopy();
  return (
    <AdminGuard>
      <PageSection title={copy.officialImport.title} subtitle={copy.officialImport.subtitle} testId="page-admin-membres-officiels">
        <OfficialMembersRoster />
      </PageSection>
    </AdminGuard>
  );
}

export function FieldLine({ testId, label, value, fallback }) {
  return (
    <p data-testid={testId}>
      {label} : {displayValue(value, fallback)}
    </p>
  );
}

export function OfficialMemberHealthGuard({ record }) {
  const { username, role } = useAuth();
  const { copy } = useMembershipCopy();
  const membershipStore = useMembershipStore();
  const canHealth = canViewHealthRecord({ role, username }, record, membershipStore.settings);
  if (!record?.health) {
    return <p data-testid="member-360-health-empty">{copy.admin.toComplete}</p>;
  }
  if (!canHealth) {
    return (
      <p className="membership-restricted" data-testid="member-360-health-hidden">
        {copy.portal.healthHidden}
      </p>
    );
  }
  return (
    <dl data-testid="member-360-health">
      {Object.entries(record.health.answers || {}).map(([key, val]) => (
        <React.Fragment key={key}>
          <dt>{key}</dt>
          <dd>{displayValue(val, copy.admin.toComplete)}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

export function CkaImportOnAdminImportPage() {
  const { copy } = useMembershipCopy();
  return (
    <GsdReadableBlock testId="cka-import-admin-panel">
      <h3>{copy.officialImport.title}</h3>
      <OfficialMembersRoster autoApply={false} />
    </GsdReadableBlock>
  );
}
